if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.swiperInstance = null;

        // Detect Swiper-based viewer
        const isSwiper = this.elements.viewer && this.elements.viewer.classList.contains('swiper');

        if (isSwiper) {
          this.initGallerySwiper();
        } else {
          // Original slider-component behavior
          if (this.elements.thumbnails) {
            this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          }
        }

        // Thumbnail click handlers (shared)
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }
      }

      initGallerySwiper() {
        const viewerEl = this.elements.viewer;
        const slides = viewerEl.querySelectorAll('.swiper-slide');

        if (slides.length <= 1) {
          viewerEl.dataset.swiperInitialized = 'true';
          return;
        }

        const init = () => {
          if (viewerEl.offsetWidth === 0) {
            setTimeout(init, 100);
            return;
          }

          this.swiperInstance = new Swiper(viewerEl, {
            direction: 'horizontal',
            loop: false,
            slidesPerView: 1,
            centeredSlides: true,
            spaceBetween: 20,
            speed: 800,
            grabCursor: true,
            updateOnWindowResize: false,
            resizeObserver: false,
            observer: false,
            observeParents: false,
            autoHeight: false,
            watchOverflow: true,
            navigation: {
              nextEl: viewerEl.querySelector('.swiper-button-next'),
              prevEl: viewerEl.querySelector('.swiper-button-prev'),
            },
            pagination: {
              el: viewerEl.querySelector('.swiper-pagination'),
              clickable: true,
              dynamicBullets: true,
            },
          });

          // Sync thumbnail on slide change
          this.swiperInstance.on('slideChange', () => {
            const activeSlide = slides[this.swiperInstance.activeIndex];
            if (!activeSlide) return;

            const mediaId = activeSlide.dataset.mediaId;
            if (this.elements.thumbnails) {
              const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              this.setActiveThumbnail(thumbnail);
              if (thumbnail) {
                this.announceLiveRegion(activeSlide, thumbnail.dataset.mediaPosition);
              }
            }

            this.playActiveMedia(activeSlide);
          });

          // ResizeObserver for responsive updates
          let resizeTimeout;
          const resizeObserver = new ResizeObserver(() => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (this.swiperInstance && !this.swiperInstance.destroyed) {
                this.swiperInstance.update();
              }
            }, 250);
          });
          resizeObserver.observe(viewerEl.parentElement);
          viewerEl.resizeObserver = resizeObserver;
          viewerEl.dataset.swiperInitialized = 'true';
        };

        // Shopify theme editor support
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          document.addEventListener(
            'shopify:section:load',
            () => {
              if (this.swiperInstance) {
                this.swiperInstance.destroy(true, true);
                this.swiperInstance = null;
              }
              viewerEl.dataset.swiperInitialized = 'false';
              init();
            },
            { once: true }
          );
        }

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', init, { once: true });
        } else {
          requestAnimationFrame(() => requestAnimationFrame(init));
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // Swiper-based viewer: navigate to matching slide
        if (this.swiperInstance) {
          const slides = this.elements.viewer.querySelectorAll('.swiper-slide');
          slides.forEach((slide, index) => {
            if (slide.dataset.mediaId === mediaId) {
              this.swiperInstance.slideTo(index);
            }
          });

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            this.setActiveThumbnail(activeThumbnail);
          }
          return;
        }

        // Fallback: original slider-component behavior
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
